/**
 * What a refused InOut write says to the person — our words for the API's
 * codes, never the server's message (the error-message passthrough guard).
 * An absence in the way is named by its dates (FR-REC-6), so it can be found
 * and corrected. One reading for both citizen apps: each renders the key in
 * its own language (`t(refusal.key)`), the overlap with its dates.
 */
import { DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import type { TFunction } from 'i18next';

/** Every refusal said in plain words — the overlap, which names a trip, apart. */
export const INOUT_REFUSAL_KEYS = [
  'inout.errors.notSetUp',
  'inout.errors.deactivated',
  'inout.errors.caseOpen',
  'inout.errors.caseOpenChange',
  'inout.errors.recordInUse',
  'inout.errors.notClosed',
  'inout.errors.noTitle',
  'inout.errors.handoffNotFound',
  'inout.errors.ruleValue',
  'inout.errors.datesOutOfOrder',
  'inout.errors.notAway',
  'inout.errors.notJoinable',
  'inout.errors.calculationRefused',
  'inout.errors.lastTitle',
  'inout.errors.confirmationClosed',
  'inout.errors.notFound',
  'inout.errors.fileNotLinkable',
  'inout.errors.agencyUnavailable',
  'inout.errors.consentRequired',
  'inout.errors.slotUnavailable',
  'inout.errors.tooCloseToMove',
  'inout.errors.notMovable',
  'inout.errors.requisitionExists',
  'inout.errors.calendarUnavailable',
  'inout.errors.calendarAvailable',
  'inout.errors.aiUnavailable',
  'inout.errors.generic',
] as const;

export type InOutRefusalKey = (typeof INOUT_REFUSAL_KEYS)[number];

/** The absence in the way, named by its dates. */
export interface InOutConflictingAbsence {
  id: string;
  departedOn: string;
  returnedOn: string | null;
}

export type InOutRefusal =
  | { key: 'inout.errors.overlap'; conflicting: InOutConflictingAbsence }
  /** It shares only a border day with that trip: the two can be joined (owner, 22 Oct 2026). */
  | { key: 'inout.errors.touches'; conflicting: InOutConflictingAbsence }
  | { key: InOutRefusalKey };

interface ApiErrorLike {
  code?: string;
  data?: Record<string, unknown>;
}

export function inoutRefusalOf(error: unknown): InOutRefusal {
  const { code, data } = (error ?? {}) as ApiErrorLike;
  switch (code) {
    case DOMAIN_ERROR_CODES.INOUT_ABSENCE_OVERLAP: {
      const conflicting = data?.['conflicting'] as
        { id?: unknown; departedOn?: unknown; returnedOn?: unknown } | undefined;
      if (typeof conflicting?.departedOn === 'string' && typeof conflicting.id === 'string') {
        return {
          key: data?.['touches'] === true ? 'inout.errors.touches' : 'inout.errors.overlap',
          conflicting: {
            id: conflicting.id,
            departedOn: conflicting.departedOn,
            returnedOn: typeof conflicting.returnedOn === 'string' ? conflicting.returnedOn : null,
          },
        };
      }
      return { key: 'inout.errors.generic' };
    }
    case DOMAIN_ERROR_CODES.INOUT_NOT_SET_UP:
      return { key: 'inout.errors.notSetUp' };
    case DOMAIN_ERROR_CODES.INOUT_DEACTIVATED:
      return { key: 'inout.errors.deactivated' };
    // A new InOut, a restore or a close: the InOut stays as it is; a pause: it keeps running.
    case DOMAIN_ERROR_CODES.INOUT_CASE_OPEN:
      switch (data?.['action']) {
        case 'new':
        case 'restore':
        case 'close':
          return { key: 'inout.errors.caseOpenChange' };
        default:
          return { key: 'inout.errors.caseOpen' };
      }
    case DOMAIN_ERROR_CODES.INOUT_RECORD_IN_USE:
      return { key: 'inout.errors.recordInUse' };
    case DOMAIN_ERROR_CODES.INOUT_HANDOFF_NOT_FOUND:
      return { key: 'inout.errors.handoffNotFound' };
    case DOMAIN_ERROR_CODES.INOUT_RULE_VALUE_UNKNOWN:
      return { key: 'inout.errors.ruleValue' };
    case DOMAIN_ERROR_CODES.INOUT_INVALID_RECORD:
      switch (data?.['problem']) {
        case 'notAway':
          return { key: 'inout.errors.notAway' };
        case 'notJoinable':
          return { key: 'inout.errors.notJoinable' };
        case 'calculationRefused':
          return { key: 'inout.errors.calculationRefused' };
        case 'lastTitle':
          return { key: 'inout.errors.lastTitle' };
        case 'notClosed':
          return { key: 'inout.errors.notClosed' };
        case 'noTitle':
          return { key: 'inout.errors.noTitle' };
        default:
          return { key: 'inout.errors.datesOutOfOrder' };
      }
    case DOMAIN_ERROR_CODES.INOUT_CONFIRMATION_CLOSED:
      return { key: 'inout.errors.confirmationClosed' };
    case DOMAIN_ERROR_CODES.INOUT_ITEM_NOT_FOUND:
      return { key: 'inout.errors.notFound' };
    // A document picked from the Vault that is no longer there to attach (D1).
    case DOMAIN_ERROR_CODES.VAULT_FILE_NOT_LINKABLE:
      return { key: 'inout.errors.fileNotLinkable' };
    case DOMAIN_ERROR_CODES.INOUT_AGENCY_UNAVAILABLE:
      return { key: 'inout.errors.agencyUnavailable' };
    case DOMAIN_ERROR_CODES.INOUT_CONSENT_REQUIRED:
      return { key: 'inout.errors.consentRequired' };
    case DOMAIN_ERROR_CODES.INOUT_SLOT_UNAVAILABLE:
      return { key: 'inout.errors.slotUnavailable' };
    case DOMAIN_ERROR_CODES.INOUT_CONSULTATION_NOT_MOVABLE:
      return data?.['reason'] === 'TOO_CLOSE'
        ? { key: 'inout.errors.tooCloseToMove' }
        : { key: 'inout.errors.notMovable' };
    case DOMAIN_ERROR_CODES.INOUT_REQUISITION_EXISTS:
      return { key: 'inout.errors.requisitionExists' };
    case DOMAIN_ERROR_CODES.PARTNER_CALENDAR_UNAVAILABLE:
      return { key: 'inout.errors.calendarUnavailable' };
    case DOMAIN_ERROR_CODES.INOUT_CALENDAR_AVAILABLE:
      return { key: 'inout.errors.calendarAvailable' };
    // The calculator (the AI service) not answering: later, nothing about the trip.
    case DOMAIN_ERROR_CODES.AI_UNAVAILABLE:
      return { key: 'inout.errors.aiUnavailable' };
    default:
      return { key: 'inout.errors.generic' };
  }
}

/** The agency's calendar cannot be read now (503): the person asks to be contacted instead. */
export function isCalendarUnavailable(error: unknown): boolean {
  return (error as ApiErrorLike | null)?.code === DOMAIN_ERROR_CODES.PARTNER_CALENDAR_UNAVAILABLE;
}

/** InOut has no rules for the country asked about — an answer, not an outage. */
export function isCountryNotServed(error: unknown): boolean {
  return (error as ApiErrorLike | null)?.code === DOMAIN_ERROR_CODES.INOUT_COUNTRY_NOT_SERVED;
}

/**
 * A refused InOut write as the person reads it, in both citizen apps: the
 * overlap names the absence in the way by its dates (`formatDate` is the
 * app's, with the calendar format); every other refusal is its key.
 */
export function inoutRefusalText(
  error: unknown,
  { t, formatDate }: { t: TFunction; formatDate: (isoDate: string) => string }
): string {
  const refusal = inoutRefusalOf(error);
  switch (refusal.key) {
    case 'inout.errors.overlap': {
      const from = formatDate(refusal.conflicting.departedOn);
      return refusal.conflicting.returnedOn
        ? t('inout.errors.overlap', { from, to: formatDate(refusal.conflicting.returnedOn) })
        : t('inout.errors.overlapOpen', { from });
    }
    case 'inout.errors.touches': {
      const from = formatDate(refusal.conflicting.departedOn);
      return refusal.conflicting.returnedOn
        ? t('inout.errors.touches', { from, to: formatDate(refusal.conflicting.returnedOn) })
        : t('inout.errors.touchesOpen', { from });
    }
    default:
      return t(refusal.key);
  }
}
